// Stakeholder system (M3) — docs/ARCHITECTURE_M3.md §1, design plan §11.
// The unified schema: meters (foundry relationship, per-market customer
// relationship) with drivers and thresholds, plus the ASK machinery — one
// decision queue shared by authored content cards and system-generated events
// (poach counters, star offers, lift-outs, acquisition approaches). Effects are
// a closed vocabulary resolved to concrete numbers at spawn time.

import type { AskDef, AskEffects, AskSource, MarketId, OpenAsk } from "../types.js";
import { stageAtLeast } from "../types.js";
import type { TickContext } from "../ctx.js";
import { charge, emit } from "../ctx.js";
import { interpolate } from "../calendar.js";
import { clamp } from "../prng.js";
import { addStar, moraleHit, removeStar } from "./talent.js";
import { executeExit } from "./ma.js";

function fmtMoney(x: number): string {
  const abs = Math.abs(x);
  if (abs >= 1e9) return `$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${Math.round(abs / 1e3)}K`;
  return `$${Math.round(abs)}`;
}

function meterOf(ctx: TickContext, source: AskSource, marketId?: MarketId): number {
  switch (source) {
    case "board":
      return ctx.s.board.confidence;
    case "foundry":
      return ctx.s.stakeholders.foundryRel;
    case "customer":
      return marketId !== undefined ? (ctx.s.stakeholders.customers[marketId] ?? 55) : 55;
    default:
      return ctx.s.talent.morale; // talent / flavor / rival gate on morale
  }
}

export function foundryDelta(ctx: TickContext, key: string): void {
  const delta = ctx.content.economy.stakeholders.foundryDeltas[key];
  if (delta === undefined) return;
  const st = ctx.s.stakeholders;
  st.foundryRel = clamp(st.foundryRel + delta, 0, 100);
}

export function customerDelta(ctx: TickContext, marketId: MarketId, key: string): void {
  const sd = ctx.content.economy.stakeholders;
  const delta = sd.customerDeltas[key];
  if (delta === undefined) return;
  const st = ctx.s.stakeholders;
  st.customers[marketId] = clamp((st.customers[marketId] ?? sd.customerStart) + delta, 0, 100);
}

/** Foundry perks/penalties: wafer-cost multiplier for COGS. */
export function waferCostMult(ctx: TickContext): number {
  const sd = ctx.content.economy.stakeholders;
  const st = ctx.s.stakeholders;
  let mult = 1;
  if (st.foundryRel >= sd.hotLotRelFloor) mult *= 1 - sd.hotLotWaferDiscount;
  else if (st.foundryRel < sd.penaltyRelCeil) mult *= 1 + sd.penaltyWaferMarkup;
  if (st.waferDiscountMonthsLeft > 0) mult *= 1 - st.waferDiscountPct;
  return mult;
}

/** Foundry perks/penalties: fab-cycle delta in months (applied at tape-out). */
export function fabCycleDelta(ctx: TickContext): number {
  const sd = ctx.content.economy.stakeholders;
  const rel = ctx.s.stakeholders.foundryRel;
  if (rel >= sd.hotLotRelFloor) return -sd.hotLotCycleMonths;
  if (rel < sd.penaltyRelCeil) return sd.penaltyCycleMonths;
  return 0;
}

/** Capacity-crunch allocation squeeze: fraction of demand you can actually supply. */
export function allocationFactor(ctx: TickContext): number {
  const sd = ctx.content.economy.stakeholders;
  const crunch = interpolate(sd.crunch, ctx.s.monthIndex);
  if (crunch < 0.5) return 1;
  return ctx.s.stakeholders.foundryRel < sd.crunchSqueezeRelFloor ? sd.crunchSqueezeFactor : 1;
}

/** Current target-share multiplier from customer relationship (market.ts). */
export function customerShareFactor(ctx: TickContext, marketId: MarketId): number {
  const sd = ctx.content.economy.stakeholders;
  const models = ctx.content.economy.models;
  const rel = ctx.s.stakeholders.customers[marketId] ?? sd.customerStart;
  return clamp(1 + (rel - sd.customerBaseline) * models.customerRelSlope, models.customerRelMin, models.customerRelMax);
}

/** Active share-boost multiplier for a market (asks / acquisitions). */
export function shareBoostFactor(ctx: TickContext, marketId: MarketId): number {
  let mult = 1;
  for (const b of ctx.s.stakeholders.shareBoosts) {
    if (b.marketId === marketId && b.monthsLeft > 0) mult *= b.mult;
  }
  return mult;
}

// ---------------------------------------------------------------------------
// The ask queue
// ---------------------------------------------------------------------------

/** Resolve {cash0}/{cash1}/... placeholders against the options' resolved cash. */
function fillCashPlaceholders(text: string, options: { effects: AskEffects }[]): string {
  return text.replace(/\{cash(\d)\}/g, (_, i) => fmtMoney(options[Number(i)]?.effects.cash ?? 0));
}

/** Open a system-generated ask (talent, M&A) with pre-computed effects. */
export function openSystemAsk(
  ctx: TickContext,
  spec: {
    source: AskSource;
    headline: string;
    body: string;
    footnote?: string;
    marketId?: MarketId;
    monthsLeft: number;
    defaultOption: number;
    options: { label: string; effects: AskEffects }[];
  },
): OpenAsk {
  const s = ctx.s;
  const ask: OpenAsk = {
    id: `ask-${s.counters.nextAsk}`,
    defId: null,
    source: spec.source,
    headline: spec.headline,
    body: fillCashPlaceholders(spec.body, spec.options),
    footnote: spec.footnote,
    marketId: spec.marketId,
    monthsLeft: spec.monthsLeft,
    defaultOption: spec.defaultOption,
    options: spec.options,
  };
  s.counters.nextAsk += 1;
  s.asks.push(ask);
  emit(ctx, "askOpened", ask.headline, { askId: ask.id, source: ask.source });
  return ask;
}

/** Apply one option's effects. The single consumer for answers AND expiries. */
export function applyAskEffects(ctx: TickContext, ask: OpenAsk, option: number): void {
  const s = ctx.s;
  const fx = ask.options[option]!.effects;
  const sd = ctx.content.economy.stakeholders;

  if (fx.cash) charge(ctx, fx.cashCategory ?? "askCash", fx.cash, `${ask.headline} — ${ask.options[option]!.label}`);
  if (fx.board) s.board.confidence = clamp(s.board.confidence + fx.board, 0, 100);
  if (fx.foundry) s.stakeholders.foundryRel = clamp(s.stakeholders.foundryRel + fx.foundry, 0, 100);
  if (fx.customer && ask.marketId !== undefined) {
    s.stakeholders.customers[ask.marketId] = clamp(
      (s.stakeholders.customers[ask.marketId] ?? sd.customerStart) + fx.customer,
      0,
      100,
    );
  }
  if (fx.morale) moraleHit(ctx, fx.morale);
  if (fx.hires) {
    for (const h of fx.hires) s.people.pools[h.discipline].productive += h.count;
  }
  if (fx.losePool) {
    const pool = s.people.pools[fx.losePool.discipline];
    pool.productive = Math.max(0, pool.productive - fx.losePool.count);
  }
  if (fx.starJoin) addStar(ctx, fx.starJoin);
  if (fx.starLeave) removeStar(ctx, fx.starLeave, "took the rival's offer.");
  if (fx.waferDiscount) {
    s.stakeholders.waferDiscountPct = fx.waferDiscount.pct;
    s.stakeholders.waferDiscountMonthsLeft = fx.waferDiscount.months;
  }
  if (fx.volumeCommit) {
    // frac commits a share of your CURRENT run-rate; explicit units commit absolutes
    let units = fx.volumeCommit.unitsPerMonth ?? 0;
    if (fx.volumeCommit.unitsPerMonthFrac !== undefined) {
      let demand = 0;
      for (const p of s.products) {
        if (!p.active) continue;
        const market = ctx.content.markets.find((mk) => mk.id === p.marketId)!;
        demand += p.share * interpolate(market.tamUnitsPerMonth, s.monthIndex);
      }
      units = Math.round(fx.volumeCommit.unitsPerMonthFrac * demand);
    }
    if (units > 0) {
      s.stakeholders.volumeCommit = {
        unitsPerMonth: units,
        monthsLeft: fx.volumeCommit.months,
        penaltyPerUnit: fx.volumeCommit.penaltyPerUnit,
      };
    }
  }
  if (fx.shareBoost) {
    const marketId = fx.shareBoost.marketId ?? ask.marketId;
    if (marketId !== undefined) {
      s.stakeholders.shareBoosts.push({ marketId, mult: fx.shareBoost.mult, monthsLeft: fx.shareBoost.months });
    }
  }
  if (fx.supportProgram && ask.marketId !== undefined && !s.stakeholders.supportPrograms.includes(ask.marketId)) {
    s.stakeholders.supportPrograms.push(ask.marketId);
  }
  if (fx.toolQualityHit) {
    const tool = s.tools.inhouse[fx.toolQualityHit.discipline];
    tool.em *= 1 - fx.toolQualityHit.amount;
  }
  if (fx.sellCompany) executeExit(ctx, fx.sellCompany.price);
}

/** The command path (commands.ts): returns a rejection reason or null. */
export function answerAsk(ctx: TickContext, askId: string, option: number): string | null {
  const s = ctx.s;
  const ask = s.asks.find((a) => a.id === askId);
  if (!ask) return "no such decision on the table";
  if (!Number.isInteger(option) || option < 0 || option >= ask.options.length) return "no such option";
  applyAskEffects(ctx, ask, option);
  s.asks = s.asks.filter((a) => a.id !== askId);
  emit(ctx, "askResolved", `${ask.headline} — ${ask.options[option]!.label}`, {
    askId,
    defId: ask.defId,
    option,
  });
  return null;
}

// ---------------------------------------------------------------------------
// Close-phase processing: meters, penalties, expiry, authored-card spawning
// ---------------------------------------------------------------------------

function trigOk(ctx: TickContext, def: AskDef, marketId?: MarketId): boolean {
  const s = ctx.s;
  const t = def.trigger;
  const m = s.monthIndex;
  if (t.fromMonth !== undefined && m < t.fromMonth) return false;
  if (t.toMonth !== undefined && m > t.toMonth) return false;
  if (t.minStage !== undefined && !stageAtLeast(s.stage, t.minStage)) return false;
  if (t.requiresProducts && !s.products.some((p) => p.active)) return false;
  if (t.requiresPublic && s.stage !== "public") return false;
  if (t.requiresStar && s.talent.stars.length === 0) return false;
  const rel = meterOf(ctx, def.source, marketId);
  if (t.minRel !== undefined && rel < t.minRel) return false;
  if (t.maxRel !== undefined && rel >= t.maxRel) return false;
  const last = s.askLog[def.id];
  if (last !== undefined) {
    if (t.oncePerGame) return false;
    if (t.cooldownMonths !== undefined && m - last < t.cooldownMonths) return false;
    if (t.cooldownMonths === undefined && t.hazardMonthly === undefined) return false; // deterministic one-shots fire once
  }
  return true;
}

/** The market a customer ask binds to: the player's biggest active franchise. */
function bindMarket(ctx: TickContext): MarketId | undefined {
  let best: MarketId | undefined;
  let bestShare = -1;
  for (const p of ctx.s.products) {
    if (!p.active) continue;
    const share = ctx.s.markets[p.marketId]?.totalShare ?? 0;
    if (share > bestShare) {
      bestShare = share;
      best = p.marketId;
    }
  }
  return best;
}

export function processStakeholdersClose(ctx: TickContext): void {
  const { s, content } = ctx;
  if (s.dead) return;
  const sd = content.economy.stakeholders;
  const st = s.stakeholders;
  const m = s.monthIndex;

  // ---- meters drift toward their baselines (memory decays slowly) ----
  st.foundryRel += (sd.foundryBaseline - st.foundryRel) * sd.foundryReversionMonthly * ctx.monthsPerTick;
  st.foundryRel = clamp(st.foundryRel, 0, 100);
  for (const id of Object.keys(st.customers)) {
    st.customers[id] = clamp(
      st.customers[id]! + (sd.customerBaseline - st.customers[id]!) * sd.customerReversionMonthly * ctx.monthsPerTick,
      0,
      100,
    );
  }

  // ---- timed state: discounts, boosts, the volume commitment ----
  if (st.waferDiscountMonthsLeft > 0) st.waferDiscountMonthsLeft = Math.max(0, st.waferDiscountMonthsLeft - ctx.monthsPerTick);
  for (const b of st.shareBoosts) b.monthsLeft -= ctx.monthsPerTick;
  st.shareBoosts = st.shareBoosts.filter((b) => b.monthsLeft > 0);
  if (st.volumeCommit) {
    const shipped = ctx.productSales.reduce((a, x) => a + x.units, 0);
    const shortfall = st.volumeCommit.unitsPerMonth * ctx.monthsPerTick - shipped;
    if (shortfall > 0) {
      const penalty = Math.round(shortfall * st.volumeCommit.penaltyPerUnit);
      charge(ctx, "volumePenalty", -penalty, "foundry volume-commitment shortfall");
    }
    st.volumeCommit.monthsLeft -= ctx.monthsPerTick;
    if (st.volumeCommit.monthsLeft <= 0) st.volumeCommit = null;
  }

  // ---- forecast credibility: a q/q collapse in wafer pull reads as a bust ----
  if (m % 3 === 0 && s.kpiHistory.length >= 6) {
    const productRev = (k: { revenue: number; nreRevenue: number }) => k.revenue - k.nreRevenue;
    const recent = s.kpiHistory.slice(-3).reduce((a, k) => a + productRev(k), 0);
    const prior = s.kpiHistory.slice(-6, -3).reduce((a, k) => a + productRev(k), 0);
    const cooledOff = st.lastForecastBustMonth === null || m - st.lastForecastBustMonth >= 6;
    if (prior > 500_000 && recent < prior * (1 - sd.forecastBustDropFrac) && cooledOff) {
      st.lastForecastBustMonth = m;
      foundryDelta(ctx, "forecastBust");
      emit(ctx, "foundrySqueeze", "Your wafer pull collapsed against forecast — the foundry rebuilt its line plan around you.", {
        foundryRel: Math.round(st.foundryRel),
      });
    }
  }

  // ---- age & expire open asks (expiry applies the default option) ----
  for (const ask of [...s.asks]) {
    ask.monthsLeft -= ctx.monthsPerTick;
    if (ask.monthsLeft > 0) continue;
    applyAskEffects(ctx, ask, ask.defaultOption);
    s.asks = s.asks.filter((a) => a.id !== ask.id);
    emit(ctx, "askExpired", `${ask.headline} — unanswered; ${ask.options[ask.defaultOption]!.label.toLowerCase()}.`, {
      askId: ask.id,
      defId: ask.defId,
    });
  }

  // ---- spawn authored cards (content order = deterministic draw order) ----
  const openContent = s.asks.filter((a) => a.defId !== null).length;
  let budget = Math.max(0, sd.askMaxOpen - openContent);
  for (const def of content.asks) {
    if (budget <= 0) break;
    if (s.asks.some((a) => a.defId === def.id)) continue;
    const marketId = def.source === "customer" ? bindMarket(ctx) : undefined;
    if (def.source === "customer" && marketId === undefined) continue;
    if (!trigOk(ctx, def, marketId)) continue;
    if (def.trigger.hazardMonthly !== undefined && !ctx.rng.bernoulli(def.trigger.hazardMonthly * ctx.monthsPerTick)) continue;

    // resolve cash scaling at spawn: "burn" cards are priced in months of burn,
    // "eraCapital" cards in 1988 dollars × the era-capital curve
    let scale = 1;
    if (def.cashScale === "eraCapital") scale = interpolate(content.economy.eraCapitalScale, m);
    if (def.cashScale === "burn") {
      const burn = s.finance.burnHistory;
      scale = Math.max(100_000, burn.length ? burn.reduce((a, b) => a + b, 0) / burn.length : 100_000);
    }
    const options = def.options.map((o) => ({
      label: o.label,
      effects: { ...o.effects, ...(o.effects.cash !== undefined ? { cash: Math.round(o.effects.cash * scale) } : {}) },
    }));
    const ask: OpenAsk = {
      id: `ask-${s.counters.nextAsk}`,
      defId: def.id,
      source: def.source,
      headline: def.headline,
      body: fillCashPlaceholders(def.body, options),
      footnote: def.footnote,
      marketId,
      monthsLeft: def.expiresMonths,
      defaultOption: def.defaultOption,
      options,
    };
    s.counters.nextAsk += 1;
    s.asks.push(ask);
    s.askLog[def.id] = m;
    budget -= 1;
    emit(ctx, "askOpened", ask.headline, { askId: ask.id, defId: def.id, source: def.source });
  }
}
